import { Component, EventEmitter, forwardRef, inject, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { EzInputComponent } from '../ez-input/ez-input.component';
import { EzInputDirective } from '../ez-input/ez-input.directive';
import { CdkOverlayOrigin, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { CdkPortal, PortalModule } from '@angular/cdk/portal';
import { AddressHint } from '../../../api/v1/models/address-hint';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { EzRightDirective } from '../ez-input/ez-right.directive';

@Component({
  selector: 'app-search-select',
  templateUrl: './search-select.component.html',
  styleUrls: ['./search-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchSelectComponent),
      multi: true,
    }
  ],
  imports: [
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    CdkOverlayOrigin,
    EzInputComponent,
    EzInputDirective,
    EzRightDirective,
    PortalModule
  ]
})
export class SearchSelectComponent implements ControlValueAccessor {
  private overlay = inject(Overlay);

  formControl: FormControl<string> = new FormControl<string>('', {nonNullable: true});

  //TODO: Change to content projection
  @Input() hints: AddressHint[] | null = null;
  @Input() isLoading: boolean = false;

  @Output() searchValue: EventEmitter<string> = new EventEmitter<string>();
  @Output() selectHint: EventEmitter<AddressHint> = new EventEmitter<AddressHint>();

  @ViewChild(CdkPortal) portal!: CdkPortal;
  @ViewChild(CdkOverlayOrigin) origin!: CdkOverlayOrigin;

  isOpen: boolean = false;
  private overlayRef: OverlayRef;

  private currentValue: string = '';

  onChange: any = (): void => {
  };
  onTouched: any = (): void => {
  };

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.formControl.disable() : this.formControl.enable();
  }

  writeValue(value: string): void {
    this.formControl.setValue(value);
    this.currentValue = value;
  }

  onInputSearch(value: string): void {
    this.searchValue.emit(value);
  }

  onOpenDropDown(input: HTMLInputElement): void {
    if(this.hints && this.hints.length === 0) {
      this.onInputSearch(this.formControl.value);
    }

    setTimeout((): void => {
      input.selectionStart = input.value.length;
      input.scrollLeft = input.scrollWidth;
    });

    this.onTouched();
    if(this.isOpen) {
      return;
    }
    this.isOpen = true;

    const config = new OverlayConfig({
      positionStrategy: this.overlay.position().flexibleConnectedTo(this.origin.elementRef.nativeElement).withPositions([{
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
      }]).withPush(false),
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: '_clear',
      width: this.origin.elementRef.nativeElement.getBoundingClientRect().width
    });

    this.overlayRef = this.overlay.create(config);
    this.overlayRef.attach(this.portal);

    this.overlayRef.backdropClick().subscribe(() => {
      this.overlayRef.detach();
      this.isOpen = false;

      this.formControl.setValue(this.currentValue);
    });
  }

  onSelectAddress(hint: AddressHint): void {
    let viewValue = hint.value;

    if(hint.street && hint.house) {
      viewValue = hint.street + ', ' + hint.house;
    }

    this.formControl.setValue(viewValue);
    this.currentValue = hint.value;

    this.onChange(hint.value);
    this.selectHint.emit(hint);

    this.overlayRef.detach();
    this.isOpen = false;
  }

  onResetHints(): void {
    this.formControl.reset();
  }
}
